import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import style from "./PublicProfile.module.css";
import { baseurl } from "../Url";
import ProfilePlaceholder from "../../assests/Profile-PNG-File.png";
import {
  MdChat,
  MdClose,
  MdPlayCircle,
  MdInsertDriveFile,
  MdLink,
  MdLocationOn,
  MdWork,
  MdSchool,
  MdPerson,
  MdStar,
  MdRocketLaunch,
  MdFlag,
  MdTrendingUp,
  MdTimeline,
} from "react-icons/md";
import { BsPersonPlus } from "react-icons/bs";
import { IoTimeOutline } from "react-icons/io5";
import { IoSend } from "react-icons/io5";
import { AiOutlineClose } from "react-icons/ai";
import Loader from "../Loder/Loader";

const PublicProfile = () => {
  const { profileName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLightbox, setImageLightbox] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [connectPending, setConnectPending] = useState(false);
  const [showMessagePanel, setShowMessagePanel] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [messageLoading, setMessageLoading] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [messageRoomId, setMessageRoomId] = useState({ firestoreId: null, mongoId: null });

  const getResumeViewUrl = (fileUrl, fileName = "") => {
    const normalizedName = String(fileName || "").toLowerCase();
    const normalizedUrl = String(fileUrl || "").toLowerCase();
    const isOfficeDoc =
      normalizedName.endsWith(".doc") ||
      normalizedName.endsWith(".docx") ||
      normalizedUrl.includes(".doc") ||
      normalizedUrl.includes(".docx");

    if (isOfficeDoc) {
      return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
    }
    return `${baseurl}/resume/view?url=${encodeURIComponent(fileUrl)}&filename=${encodeURIComponent(fileName || "resume")}`;
  };

  const isLoggedIn = !!localStorage.getItem("USERID");
  const currentUserId = localStorage.getItem("USERID");

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const rawCode = (search.get("ref") || "").trim();
    if (!rawCode) return;

    const resolveAndStoreReferral = async () => {
      try {
        const res = await fetch(`${baseurl}/referral/link?code=${encodeURIComponent(rawCode)}`);
        const data = await res.json();

        if (data?.status && data?.data) {
          const referralCode = data.data.referral_code || rawCode;
          const referrerUserId = data.data.referrer_user_id || "";
          const referralAssetCategory = data.data.referral_asset_category || "PROFILE";
          const referralAssetId = data.data.referral_asset_id || profileName || "";

          // Requested keys
          localStorage.setItem("referral_code", referralCode);
          localStorage.setItem("referrer_user_id", referrerUserId);
          localStorage.setItem("referral_asset_category", referralAssetCategory);
          localStorage.setItem("referral_asset_id", referralAssetId);

          // Keep compatibility with existing flows
          localStorage.setItem("duno_referral_code", referralCode);
          localStorage.setItem("duno_referral_user_id", referrerUserId);
          localStorage.setItem("duno_referral_asset_category", referralAssetCategory);
          localStorage.setItem("duno_referral_asset_id", referralAssetId);
        } else {
          // Still preserve referral code when lookup fails
          localStorage.setItem("referral_code", rawCode);
          localStorage.setItem("duno_referral_code", rawCode);
        }
      } catch (err) {
        // Preserve raw code even if API lookup fails (network/CORS/etc)
        localStorage.setItem("referral_code", rawCode);
        localStorage.setItem("duno_referral_code", rawCode);
      }
    };

    resolveAndStoreReferral();
  }, [location.search, profileName]);

  // Record referral visit for profile links so referee_actions is populated.
  useEffect(() => {
    if (typeof window === "undefined" || !currentUserId) return;

    const referralCode = localStorage.getItem("duno_referral_code") || localStorage.getItem("referral_code");
    const referrerUserId = localStorage.getItem("duno_referral_user_id") || localStorage.getItem("referrer_user_id");
    const assetCategory =
      localStorage.getItem("duno_referral_asset_category") ||
      localStorage.getItem("referral_asset_category") ||
      "PROFILE";
    const assetId =
      localStorage.getItem("duno_referral_asset_id") ||
      localStorage.getItem("referral_asset_id") ||
      profileName;

    if (!referralCode || !referrerUserId || !assetId) return;
    if (String(referrerUserId) === String(currentUserId)) return;

    const dedupeKey = `duno_referral_visit_recorded_${referralCode}_${currentUserId}_${assetId}`;
    if (sessionStorage.getItem(dedupeKey) === "1") return;

    fetch(`${baseurl}/referral/record-action`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        referrer_user_id: referrerUserId,
        referee_id: currentUserId,
        referee_username: localStorage.getItem("username") || localStorage.getItem("email") || "",
        referral_asset_category: assetCategory,
        referral_asset_id: assetId,
        action: "LINK_VISIT",
      }),
    })
      .then(() => {
        sessionStorage.setItem(dedupeKey, "1");
      })
      .catch((err) => {
        console.warn("Failed to record profile referral visit:", err);
      });
  }, [currentUserId, profileName]);

  useEffect(() => {
    if (!profileName) {
      setError("Profile not found");
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(
          `${baseurl}/profile/public/${encodeURIComponent(profileName)}`
        );
        const data = await res.json();

        if (data.status && data.data) {
          setProfile(data.data);
        } else {
          setError(data.description || "Profile not found");
        }
      } catch (err) {
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profileName]);

  useEffect(() => {
    if (!isLoggedIn || !profile?.user_id || currentUserId === profile?.user_id) return;
    const fetchConnectionStatus = async () => {
      try {
        const res = await fetch(`${baseurl}/user/connection/status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: currentUserId,
            connect_user_id: profile.user_id,
          }),
        });
        const data = await res.json();
        if (data.status && data.data) {
          setConnectionStatus(data.data.connection_status);
          setConnectPending(data.data.request_send);
        }
      } catch {
        setConnectionStatus("none");
      }
    };
    fetchConnectionStatus();
  }, [isLoggedIn, profile?.user_id, currentUserId]);

  const checkUserAndProceed = async (onSuccess) => {
    const userId = localStorage.getItem("USERID");
    if (!userId) {
      navigate("/login", { state: { from: `/profile/${profileName}` } });
      return;
    }
    try {
      const res = await fetch(`${baseurl}/check/user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json();
      if (data.status) {
        onSuccess?.();
      } else {
        navigate("/login", { state: { from: `/profile/${profileName}` } });
      }
    } catch {
      navigate("/login", { state: { from: `/profile/${profileName}` } });
    }
  };

  const getAuthToken = () => {
    const raw = localStorage.getItem("access-token");
    if (!raw) return null;
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return parsed?.token ?? parsed?.access_token ?? (typeof parsed === "string" ? parsed : null) ?? raw;
    } catch {
      return raw;
    }
  };

  const openMessagePanel = () => {
    if (currentUserId === profile?.user_id) {
      navigate("/profile");
      return;
    }
    setShowMessagePanel(true);
    setMessageSent(false);
    setMessageText("");
    setMessageRoomId({ firestoreId: null, mongoId: null });
    createRoomForChat();
  };

  const createRoomForChat = async () => {
    const token = getAuthToken();
    const orgId = localStorage.getItem("ORGID") || "";
    const username = localStorage.getItem("username") || "";
    if (!currentUserId || !profile?.user_id || !token) return;
    setMessageLoading(true);
    try {
      const res = await fetch(`${baseurl}/roomcreation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          org_id: orgId,
          sender_id: currentUserId,
          sender_name: username,
          receiver_id: profile.user_id,
          receiver_name: profile.full_name || "",
        }),
      });
      const data = await res.json();
      if (data.status && data.data?.[0]) {
        setMessageRoomId({
          firestoreId: data.data[0].firestore_roomid,
          mongoId: data.data[0].mongoroom_id,
        });
      }
    } catch (err) {
      console.error("Room create error:", err);
    } finally {
      setMessageLoading(false);
    }
  };

  const sendMessage = async () => {
    const text = (messageText || "").trim();
    if (!text || messageLoading) return;
    const token = getAuthToken();
    const orgId = localStorage.getItem("ORGID") || "";
    const username = localStorage.getItem("username") || "";
    const { firestoreId, mongoId } = messageRoomId;
    if (!firestoreId || !mongoId || !token) return;
    setMessageLoading(true);
    try {
      const res = await fetch(`${baseurl}/chat/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          org_id: orgId,
          sender_id: currentUserId,
          sender_name: username,
          receiver_id: profile.user_id,
          receiver_name: profile.full_name || "",
          content_type: "text",
          content: text,
          firestore_roomid: firestoreId,
          mongoroom_id: mongoId,
        }),
      });
      const data = await res.json();
      if (data.status || data.success) {
        setMessageText("");
        setMessageSent(true);
      }
    } catch (err) {
      console.error("Send message error:", err);
    } finally {
      setMessageLoading(false);
    }
  };

  const handleChat = () => {
    checkUserAndProceed(() => {
      if (currentUserId === profile?.user_id) {
        navigate("/profile");
        return;
      }
      openMessagePanel();
    });
  };

  const handleConnect = () => {
    checkUserAndProceed(async () => {
      if (!profile?.user_id || connectPending || connectionStatus === "accepted") return;
      try {
        const key = JSON.parse(localStorage.getItem("access-token"));
        const res = await fetch(`${baseurl}/user/connection/send`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${key}`,
          },
          body: JSON.stringify({
            user_id: currentUserId,
            username: localStorage.getItem("username") || "",
            connect_user_id: profile.user_id,
            connect_username: profile.full_name || "",
            org_id: localStorage.getItem("ORGID") || "",
          }),
        });
        const data = await res.json();
        if (data.status || data.success) {
          setConnectPending(true);
        }
      } catch (err) {
        console.error("Connection send error:", err);
      }
    });
  };

  const getSkillLabel = (skill) => {
    if (typeof skill === "string") return skill;
    return skill?.skill || skill?.title || "";
  };
  const getLinkUrl = (link) => {
    if (!link) return "";
    if (typeof link === "string") return link;
    return link.url || link.link || "";
  };
  const getLinkLabel = (link) => {
    if (!link) return "";
    if (typeof link === "string") return link;
    return link.name || link.title || link.type || getLinkUrl(link) || "Profile Link";
  };

  const getYoutubeThumbnail = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
  };

  if (loading) {
    return (
      <div className={`${style.container} ${style.loadingState}`}>
        <Loader />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className={`${style.container} ${style.errorState}`}>
        <div className={style.errorBox}>
          <p>{error || "Profile not found"}</p>
          <button onClick={() => navigate("/")}>Go to Home</button>
        </div>
      </div>
    );
  }

  const isOwner = currentUserId === profile.user_id;

  return (
    <div className={style.container}>
      <div className={style.content}>
        <div className={style.profileCard}>
        {/* Header card with profile image and name */}
        <header className={style.header}>
          <div className={style.headerCard}>
            <div className={style.headerTop}>
              <div
                className={style.profileImage}
                onClick={() =>
                  profile.profile_image && setImageLightbox(profile.profile_image)
                }
                title={profile.profile_image ? "Click to view full size" : undefined}
                role={profile.profile_image ? "button" : undefined}
              >
                <img
                  src={profile.profile_image || ProfilePlaceholder}
                  alt={profile.full_name}
                />
              </div>
              <div className={style.headerInfo}>
                <h1>{profile.full_name || "Anonymous"}</h1>
                {(profile.city || profile.country) && (
                  <p className={style.location}>
                    <MdLocationOn size={16} />
                    {[profile.city, profile.country].filter(Boolean).join(", ")}
                  </p>
                )}
                <div className={`${style.actions} ${isOwner ? style.actionsOwner : ""}`}>
                  {!isOwner && (
                    <>
                      {(connectionStatus === "accepted" || connectPending) ? (
                        <button
                          className={`${style.connectBtn} ${style.connectBtnPending}`}
                          disabled
                          title={connectionStatus === "accepted" ? "Connected" : "Pending"}
                        >
                          {connectionStatus === "accepted" ? (
                            "Connected"
                          ) : (
                            <>
                              <IoTimeOutline className={style.connectBtnIcon} />
                              Pending
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          className={style.connectBtn}
                          onClick={handleConnect}
                          title={isLoggedIn ? "Connect" : "Login to connect"}
                        >
                          <BsPersonPlus className={style.connectBtnIcon} />
                          Connect
                        </button>
                      )}
                      <button
                        className={style.chatBtn}
                        onClick={handleChat}
                        title={isLoggedIn ? "Chat" : "Login to chat"}
                      >
                        <MdChat size={24} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className={style.profileBody}>
          <div className={style.cardsGrid}>
            {/* About card */}
            {profile.summary && (
              <div className={style.infoCard}>
                <div className={style.infoCardTitle}>
                  <MdPerson size={18} />
                  <span>About</span>
                </div>
                <p className={style.summary}>{profile.summary}</p>
              </div>
            )}

            {/* Skills card */}
            {profile.skills && profile.skills.length > 0 && (
              <div className={style.infoCard}>
                <div className={style.infoCardTitle}>
                  <MdStar size={18} />
                  <span>Skills</span>
                </div>
                <div className={style.skillsGrid}>
                  {profile.skills.map((skill, i) => (
                    <span key={i} className={style.skillTag}>
                      {getSkillLabel(skill)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Work Experience card (left of CTA on second row) */}
            {profile.work_experience && profile.work_experience.length > 0 && (
              <div className={`${style.infoCard} ${style.experienceCard}`}>
                <div className={style.infoCardTitle}>
                  <MdWork size={18} />
                  <span>Work Experience</span>
                </div>
                <div className={style.experienceList}>
                  {profile.work_experience.map((exp, i) => (
                    <div key={i} className={style.experienceItem}>
                      <strong>{exp.role || exp.title}</strong>
                      {exp.company && (
                        <span className={style.company}> at {exp.company}</span>
                      )}
                      {(exp.start_year || exp.end_year) && (
                        <p className={style.period}>
                          {exp.start_year}
                          {exp.end_year ? ` – ${exp.end_year}` : " – Present"}
                        </p>
                      )}
                      {exp.description && (
                        <p className={style.desc}>{exp.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education card */}
            {profile.education && profile.education.length > 0 && (
              <div className={style.infoCard}>
                <div className={style.infoCardTitle}>
                  <MdSchool size={18} />
                  <span>Education</span>
                </div>
                <div className={style.educationList}>
                  {profile.education.map((edu, i) => (
                    <div key={i} className={style.educationItem}>
                      <strong>{edu.degree || "Degree"}</strong>
                      {edu.school && <span className={style.company}> at {edu.school}</span>}
                      {(edu.start_year || edu.end_year) && (
                        <p className={style.period}>
                          {edu.start_year}
                          {edu.end_year ? ` - ${edu.end_year}` : ""}
                        </p>
                      )}
                      {edu.major && <p className={style.desc}>Major: {edu.major}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Projects card */}
            {profile.projects && profile.projects.length > 0 && (
              <div className={style.infoCard}>
                <div className={style.infoCardTitle}>
                  <MdRocketLaunch size={18} />
                  <span>Projects</span>
                </div>
                <div className={style.experienceList}>
                  {profile.projects.map((project, i) => (
                    <div key={i} className={style.experienceItem}>
                      <strong>{project.project_name || "Project"}</strong>
                      {(project.start_year || project.end_year) && (
                        <p className={style.period}>
                          {project.start_year}
                          {project.end_year ? ` - ${project.end_year}` : ""}
                        </p>
                      )}
                      {project.description && (
                        <p className={style.desc}>{project.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links card */}
            {profile.links && profile.links.length > 0 && (
              <div className={style.infoCard}>
                <div className={style.infoCardTitle}>
                  <MdLink size={18} />
                  <span>Links</span>
                </div>
                <div className={style.linksList}>
                  {profile.links.map((item, i) => {
                    const href = getLinkUrl(item);
                    if (!href) return null;
                    return (
                      <a
                        key={i}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={style.linkItem}
                      >
                        <MdLink size={16} />
                        <span>{getLinkLabel(item)}</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Unlock potential card (right of Work Experience) */}
            <div className={style.ctaCard}>
              <h3 className={style.ctaTitle}>Unlock Your Full Potential</h3>
              <div className={style.ctaList}>
                <div className={`${style.ctaItem} ${style.ctaItemPurple}`}>
                  <div className={style.ctaIconWrap}>
                    <MdRocketLaunch />
                  </div>
                  <div className={style.ctaText}>
                    <div className={style.ctaItemTitle}>Unlock Your Full Potential</div>
                    <div className={style.ctaItemSubtitle}>Discover new opportunities</div>
                  </div>
                </div>
                <div className={`${style.ctaItem} ${style.ctaItemBlue}`}>
                  <div className={style.ctaIconWrap}>
                    <MdFlag />
                  </div>
                  <div className={style.ctaText}>
                    <div className={style.ctaItemTitle}>Set Clear Goals</div>
                    <div className={style.ctaItemSubtitle}>Achieve your dreams</div>
                  </div>
                </div>
                <div className={`${style.ctaItem} ${style.ctaItemOrange}`}>
                  <div className={style.ctaIconWrap}>
                    <MdTrendingUp />
                  </div>
                  <div className={style.ctaText}>
                    <div className={style.ctaItemTitle}>Accelerate Growth</div>
                    <div className={style.ctaItemSubtitle}>Level up your career</div>
                  </div>
                </div>
                <div className={`${style.ctaItem} ${style.ctaItemGreen}`}>
                  <div className={style.ctaIconWrap}>
                    <MdTimeline />
                  </div>
                  <div className={style.ctaText}>
                    <div className={style.ctaItemTitle}>Track Progress</div>
                    <div className={style.ctaItemSubtitle}>Monitor your success</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Videos – full width card below grid */}
          {profile.videos && profile.videos.length > 0 && (
            <section className={`${style.section} ${style.videosSection}`}>
              <div className={style.infoCard}>
                <div className={style.infoCardTitle}>
                  <MdPlayCircle size={18} />
                  <span>Videos</span>
                </div>
                <div className={style.mediaGrid}>
                  {profile.videos.map((video, i) => {
                    const label = video.title || video.file_name || "Video";
                    const isYoutube = video.file_type === "youtube";
                    const isMp4 =
                      video.file_url &&
                      /\.(mp4|webm|ogg)$/i.test(video.file_url);

                    return (
                      <div key={i} className={style.videoCard}>
                        <div className={style.videoCardThumb}>
                          {isYoutube ? (
                            <iframe
                              src={video.file_url}
                              title={label}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          ) : isMp4 ? (
                            <video
                              src={video.file_url}
                              controls
                              playsInline
                            />
                          ) : (
                            <div className={style.videoCardPlaceholder} />
                          )}
                        </div>
                        <div className={style.videoCardLabel}>{label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* Resume – full width card */}
          {profile.resume && profile.resume.file_url && (
            <section className={`${style.section} ${style.videosSection}`}>
              <div className={style.infoCard}>
                <div className={style.infoCardTitle}>
                  <MdInsertDriveFile size={18} />
                  <span>Resume</span>
                </div>
                <a
                  href={getResumeViewUrl(profile.resume.file_url, profile.resume.file_name || "resume")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={style.resumeLink}
                >
                  <MdInsertDriveFile size={18} />
                  <span className={style.resumeFileName}>
                    {profile.resume.file_name || "View Resume"}
                  </span>
                </a>
              </div>
            </section>
          )}

          {/* Uploaded Documents – full width card */}
          {profile.documents && profile.documents.length > 0 && (
            <section className={`${style.section} ${style.videosSection}`}>
              <div className={style.infoCard}>
                <div className={style.infoCardTitle}>
                  <MdInsertDriveFile size={18} />
                  <span>Uploaded Documents</span>
                </div>
                <div className={style.docGrid}>
                  {profile.documents.map((doc, i) => {
                    const fileName = doc.file_name || doc.title || "Document";
                    const fileUrl = doc.file_url || "";
                    const extension = (fileName.split(".").pop() || "doc").slice(0, 4);
                    return (
                      <a
                        key={i}
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={style.docCard}
                      >
                        <div className={style.docCardIcon}>
                          <MdInsertDriveFile size={28} />
                          <span className={style.docCardExt}>{extension}</span>
                        </div>
                        <div className={style.docCardLabel}>{fileName}</div>
                        <div className={style.docCardAction}>View Document</div>
                      </a>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
      </div>
      </div>
      </div>

      {/* Message sending panel */}
      {showMessagePanel && (
        <>
          <div
            className={style.messagePanelOverlay}
            onClick={() => setShowMessagePanel(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Escape" && setShowMessagePanel(false)}
            aria-label="Close"
          />
          <div className={style.messagePanel} onClick={(e) => e.stopPropagation()}>
            <div className={style.messagePanelHead}>
              <div className={style.messagePanelTitle}>
                <span className={style.messagePanelToLabel}>To: </span>
                <span>{profile?.full_name || "User"}</span>
              </div>
              <button
                type="button"
                className={style.messagePanelClose}
                onClick={() => setShowMessagePanel(false)}
                aria-label="Close"
              >
                <AiOutlineClose size={22} />
              </button>
            </div>
            <div className={style.messagePanelContent}>
              {messageLoading && !messageRoomId.firestoreId ? (
                <div className={style.messagePanelLoading}>
                  <Loader />
                  <p>Opening conversation…</p>
                </div>
              ) : (
                <>
                  {messageSent && (
                    <p className={style.messageSentNote}>Message sent.</p>
                  )}
                  <div className={style.messagePanelInputWrap}>
                    <textarea
                      className={style.messagePanelTextarea}
                      placeholder="Type your message…"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      rows={4}
                      disabled={messageLoading}
                    />
                    <button
                      type="button"
                      className={style.messagePanelSendBtn}
                      onClick={sendMessage}
                      disabled={!messageText.trim() || messageLoading}
                    >
                      <IoSend size={20} />
                      Send
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Image Lightbox */}
      {imageLightbox && (
        <div
          className={style.lightbox}
          onClick={() => setImageLightbox(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Escape" && setImageLightbox(null)}
        >
          <button
            className={style.lightboxClose}
            onClick={() => setImageLightbox(null)}
            aria-label="Close"
          >
            <MdClose size={28} />
          </button>
          <img
            src={imageLightbox}
            alt="Profile"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PublicProfile;
